import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Plus, Minus, Check, Music, GraduationCap, Sparkles, Users, X, CreditCard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RentalsPage = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, isMember } = useAuth();
  
  const [step, setStep] = useState(1);
  const [snakes, setSnakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Rental form state
  const [rentalType, setRentalType] = useState('');
  const [primarySnakeId, setPrimarySnakeId] = useState('');
  const [additionalSnakeIds, setAdditionalSnakeIds] = useState([]);
  const [date, setDate] = useState(undefined);
  const [startTime, setStartTime] = useState('10:00');
  const [durationHours, setDurationHours] = useState(1);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [guests, setGuests] = useState([]);
  const [newGuest, setNewGuest] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  
  // Price calculation
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  
  // Rental result
  const [rental, setRental] = useState(null);

  // Cost split calculation
  const calculateSplit = (total, numPeople) => {
    if (numPeople <= 1) return [total];
    const totalCents = Math.round(total * 100);
    const baseCents = Math.floor(totalCents / numPeople);
    const remainder = totalCents - baseCents * numPeople;
    return Array.from({ length: numPeople }, (_, i) =>
      (baseCents + (i < remainder ? 1 : 0)) / 100
    );
  };

  const addGuest = () => {
    if (newGuest && !guests.includes(newGuest)) {
      setGuests([...guests, newGuest]);
      setNewGuest('');
    }
  };

  const removeGuest = (email) => {
    setGuests(guests.filter(g => g !== email));
  };

  useEffect(() => {
    const fetchSnakes = async () => {
      try {
        const response = await fetch(`${API}/snakes`);
        const data = await response.json();
        setSnakes(data);
      } catch (error) {
        console.error('Error fetching snakes:', error);
        toast.error('Failed to load snakes');
      } finally {
        setLoading(false);
      }
    };
    fetchSnakes();
  }, []);

  useEffect(() => {
    if (primarySnakeId && rentalType) {
      calculatePrice();
    }
  }, [primarySnakeId, additionalSnakeIds, rentalType, durationHours]);

  const calculatePrice = async () => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/rentals/calculate-price`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          rental_type: rentalType,
          primary_snake_id: primarySnakeId,
          additional_snake_ids: additionalSnakeIds,
          duration_hours: durationHours
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPriceBreakdown(data);
      }
    } catch (error) {
      console.error('Error calculating price:', error);
    }
  };

  const addAdditionalSnake = (snakeId) => {
    if (!additionalSnakeIds.includes(snakeId) && snakeId !== primarySnakeId) {
      setAdditionalSnakeIds([...additionalSnakeIds, snakeId]);
    }
  };

  const removeAdditionalSnake = (snakeId) => {
    setAdditionalSnakeIds(additionalSnakeIds.filter(id => id !== snakeId));
  };

  const handleSubmitRental = async () => {
    if (!date) {
      toast.error('Please select a date');
      return;
    }

    // If not logged in, require name and email
    if (!isAuthenticated && (!guestName || !guestEmail)) {
      toast.error('Please provide your name and email');
      return;
    }

    setSubmitting(true);

    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/rentals`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          rental_type: rentalType,
          primary_snake_id: primarySnakeId,
          additional_snake_ids: additionalSnakeIds,
          date: format(date, 'yyyy-MM-dd'),
          start_time: startTime,
          duration_hours: durationHours,
          location,
          description,
          guests,
          guest_name: !isAuthenticated ? guestName : undefined,
          guest_email: !isAuthenticated ? guestEmail : undefined
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create rental');
      }

      const rentalData = await response.json();
      setRental(rentalData);
      setStep(5);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStripePayment = async () => {
    if (!rental) return;
    setProcessingPayment(true);
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API}/rentals/checkout`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          rental_id: rental.rental_id,
          origin_url: window.location.origin,
          payment_method: 'stripe'
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create payment session');
      }

      const data = await response.json();
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.message);
      setProcessingPayment(false);
    }
  };

  const primarySnake = snakes.find(s => s.snake_id === primarySnakeId);

  const rentalTypes = [
    {
      id: 'music_video',
      title: 'Music Videos',
      icon: <Music className="w-8 h-8" />,
      description: 'Professional snake rentals for music video productions',
      pricing: '1-2 snakes: $50-75/hr | 3-4: +$15/hr | 5-6: +$30/hr'
    },
    {
      id: 'educational',
      title: 'Educational Parties',
      icon: <GraduationCap className="w-8 h-8" />,
      description: 'Educational events, school visits, and learning experiences',
      pricing: '1-3 snakes: $50/hr | 4-6 snakes: +$25/hr'
    },
    {
      id: 'other',
      title: 'Other Projects',
      icon: <Sparkles className="w-8 h-8" />,
      description: 'Film shoots, art projects, special events, and more',
      pricing: '$50/hr + $5/hr per additional snake'
    }
  ];

  const steps = [
    { num: 1, label: 'Project Type' },
    { num: 2, label: 'Select Snake' },
    { num: 3, label: 'Date & Time' },
    { num: 4, label: 'Details' },
    { num: 5, label: 'Payment' }
  ];

  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="rentals-page">
      <Navbar />
      
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Step Indicator */}
          <div className="flex justify-between mb-12 relative">
            <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-border -z-10" />
            {steps.map((s) => (
              <div
                key={s.num}
                className={`flex flex-col items-center ${step >= s.num ? 'text-primary' : 'text-muted-foreground'}`}
              >
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                    step > s.num
                      ? 'bg-primary text-primary-foreground'
                      : step === s.num
                      ? 'bg-primary/20 border-2 border-primary text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid={`rental-step-${s.num}`}
                >
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className="text-xs mt-2 hidden sm:block">{s.label}</span>
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1: Project Type */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Rent Snakes for Your Project</h1>
                  <p className="text-muted-foreground">Select the type of project you're working on</p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {rentalTypes.map((type) => (
                    <Card
                      key={type.id}
                      className={`cursor-pointer transition-all p-6 text-center ${
                        rentalType === type.id
                          ? 'ring-2 ring-primary border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => setRentalType(type.id)}
                      data-testid={`rental-type-${type.id}`}
                    >
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                        {type.icon}
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{type.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{type.description}</p>
                      <p className="text-xs text-primary font-medium">{type.pricing}</p>
                      {rentalType === type.id && (
                        <div className="mt-4">
                          <Check className="w-6 h-6 mx-auto text-primary" />
                        </div>
                      )}
                    </Card>
                  ))}
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!rentalType}
                    className="btn-primary"
                    data-testid="rental-step1-next"
                  >
                    Next: Select Snake
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Select Snake */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Choose Your Snake(s)</h1>
                  <p className="text-muted-foreground">
                    {rentalType === 'music_video' 
                      ? '1-2 snakes included | 3-4 snakes: +$15/hr | 5-6 snakes: +$30/hr'
                      : rentalType === 'educational'
                      ? '1-3 snakes: $50/hr | 4-6 snakes: +$25/hr'
                      : '$50/hr base rate + $5/hr per additional snake'}
                  </p>
                </div>

                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="h-64 rounded-xl image-loading" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {snakes.map((snake) => {
                      const rate = rentalType === 'music_video' 
                        ? (snake.length_ft >= 5 ? 75 : 50)
                        : 50;
                      return (
                        <Card
                          key={snake.snake_id}
                          className={`cursor-pointer transition-all overflow-hidden ${
                            primarySnakeId === snake.snake_id
                              ? 'ring-2 ring-primary border-primary'
                              : 'border-border hover:border-primary/50'
                          }`}
                          onClick={() => setPrimarySnakeId(snake.snake_id)}
                          data-testid={`rental-snake-${snake.snake_id}`}
                        >
                          <div className="relative h-48">
                            <img
                              src={snake.image_url}
                              alt={snake.name}
                              className="w-full h-full object-cover"
                            />
                            {primarySnakeId === snake.snake_id && (
                              <div className="absolute top-2 right-2 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                                <Check className="w-5 h-5 text-primary-foreground" />
                              </div>
                            )}
                          </div>
                          <CardContent className="p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="font-semibold">{snake.name}</h3>
                                <p className="text-sm text-muted-foreground">
                                  {snake.length_ft}ft • {snake.color}
                                </p>
                              </div>
                              <span className="price-tag px-2 py-1 rounded text-sm">
                                ${rate}/hr
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                {/* Additional Snakes */}
                {primarySnakeId && (
                  <div className="mt-8">
                    <h3 className="text-xl font-semibold mb-4">
                      {rentalType === 'music_video' 
                        ? `Add More Snakes (${1 + additionalSnakeIds.length}/6 selected) ${additionalSnakeIds.length >= 1 && additionalSnakeIds.length < 2 ? '- 2nd snake FREE!' : ''}`
                        : rentalType === 'educational'
                        ? `Add More Snakes (${1 + additionalSnakeIds.length}/6 selected) ${additionalSnakeIds.length >= 1 && additionalSnakeIds.length < 3 ? '- Up to 3 snakes FREE!' : ''}`
                        : 'Add More Snakes (+$5/hr each)'}
                    </h3>
                    {rentalType === 'music_video' && (
                      <p className="text-sm text-muted-foreground mb-4">
                        1-2 snakes: No extra cost | 3-4 snakes: +$15/hr | 5-6 snakes: +$30/hr
                      </p>
                    )}
                    {rentalType === 'educational' && (
                      <p className="text-sm text-muted-foreground mb-4">
                        1-3 snakes: No extra cost | 4-6 snakes: +$25/hr
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      {snakes
                        .filter(s => s.snake_id !== primarySnakeId)
                        .map((snake) => {
                          const isSelected = additionalSnakeIds.includes(snake.snake_id);
                          const maxSnakes = (rentalType === 'music_video' || rentalType === 'educational') ? 5 : 99;
                          const canAdd = additionalSnakeIds.length < maxSnakes;
                          return (
                            <Button
                              key={snake.snake_id}
                              variant={isSelected ? 'default' : 'outline'}
                              size="sm"
                              onClick={() =>
                                isSelected
                                  ? removeAdditionalSnake(snake.snake_id)
                                  : canAdd && addAdditionalSnake(snake.snake_id)
                              }
                              disabled={!isSelected && !canAdd}
                              className={isSelected ? 'bg-primary' : ''}
                              data-testid={`rental-add-snake-${snake.snake_id}`}
                            >
                              {isSelected ? (
                                <Minus className="w-4 h-4 mr-1" />
                              ) : (
                                <Plus className="w-4 h-4 mr-1" />
                              )}
                              {snake.name}
                            </Button>
                          );
                        })}
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="rental-step2-back">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={!primarySnakeId}
                    className="btn-primary"
                    data-testid="rental-step2-next"
                  >
                    Next: Date & Time
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Date & Time */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Select Date & Time</h1>
                  <p className="text-muted-foreground">When do you need the snake(s)?</p>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <Label className="text-lg mb-4 block">Select Date</Label>
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(date) => date < new Date()}
                      className="rounded-md border border-border mx-auto"
                      data-testid="rental-calendar"
                    />
                  </div>

                  <div className="space-y-6">
                    <div>
                      <Label className="text-lg mb-4 block">Start Time</Label>
                      <Select value={startTime} onValueChange={setStartTime}>
                        <SelectTrigger data-testid="rental-time-select">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((time) => (
                            <SelectItem key={time} value={time}>
                              {time}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-lg mb-4 block">Duration (Hours)</Label>
                      <div className="flex items-center space-x-4">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setDurationHours(Math.max(1, durationHours - 1))}
                          data-testid="rental-duration-decrease"
                        >
                          <Minus className="w-4 h-4" />
                        </Button>
                        <span className="text-2xl font-semibold w-16 text-center" data-testid="rental-duration-value">
                          {durationHours}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setDurationHours(durationHours + 1)}
                          data-testid="rental-duration-increase"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    {date && (
                      <Card className="p-4 bg-primary/10 border-primary/30">
                        <p className="text-sm text-muted-foreground">Selected:</p>
                        <p className="font-semibold">
                          {format(date, 'EEEE, MMMM d, yyyy')} at {startTime}
                        </p>
                        <p className="text-sm text-primary">Duration: {durationHours} hour(s)</p>
                      </Card>
                    )}
                  </div>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)} data-testid="rental-step3-back">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(4)}
                    disabled={!date}
                    className="btn-primary"
                    data-testid="rental-step3-next"
                  >
                    Next: Details
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Details */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Project Details</h1>
                  <p className="text-muted-foreground">Tell us more about your project</p>
                </div>

                <div className="space-y-6">
                  <div>
                    <Label htmlFor="location">Location / Venue</Label>
                    <Input
                      id="location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Enter the address or venue name"
                      className="mt-2"
                      data-testid="rental-location-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Project Description</Label>
                    <Textarea
                      id="description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Describe your project, any special requirements, or additional information..."
                      className="mt-2 min-h-[120px]"
                      data-testid="rental-description-textarea"
                    />
                  </div>

                  {/* Guest Splitting (Members Only) */}
                  {isMember ? (
                    <div>
                      <Label className="flex items-center mb-4">
                        <Users className="w-5 h-5 mr-2 text-primary" />
                        Add Guests & Split Costs
                        <span className="member-badge ml-2">Member Perk</span>
                      </Label>
                      <div className="flex gap-2 mb-4">
                        <Input
                          type="email"
                          value={newGuest}
                          onChange={(e) => setNewGuest(e.target.value)}
                          placeholder="Guest email address"
                          data-testid="rental-guest-email-input"
                        />
                        <Button onClick={addGuest} variant="outline" data-testid="rental-add-guest-btn">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                      {guests.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {guests.map((guest) => (
                            <span
                              key={guest}
                              className="bg-muted px-3 py-1 rounded-full text-sm flex items-center"
                            >
                              {guest}
                              <button
                                onClick={() => removeGuest(guest)}
                                className="ml-2 text-muted-foreground hover:text-foreground"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      {guests.length > 0 && priceBreakdown && (
                        <div className="mt-4 p-4 bg-primary/10 rounded-lg border border-primary/30" data-testid="rental-cost-split-preview">
                          <p className="text-sm font-semibold mb-2 flex items-center">
                            <Users className="w-4 h-4 mr-2" />
                            Cost Split ({guests.length + 1} people):
                          </p>
                          <div className="space-y-1">
                            {calculateSplit(priceBreakdown.total, guests.length + 1).map((amount, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-muted-foreground">
                                  {i === 0 ? `You (${user?.name || 'Member'})` : guests[i - 1]}
                                </span>
                                <span className="font-medium text-primary">${amount.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : !isAuthenticated ? (
                    <div className="space-y-4" data-testid="rental-guest-info-section">
                      <Label className="text-lg font-semibold block">Your Contact Info</Label>
                      <p className="text-sm text-muted-foreground -mt-2">Required to confirm your rental</p>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="rentalGuestName">Your Name</Label>
                          <Input
                            id="rentalGuestName"
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            placeholder="Full name"
                            className="mt-1"
                            data-testid="rental-guest-name-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="rentalGuestEmail">Your Email</Label>
                          <Input
                            id="rentalGuestEmail"
                            type="email"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            placeholder="email@example.com"
                            className="mt-1"
                            data-testid="rental-guest-email-field"
                          />
                        </div>
                      </div>
                      <Card className="p-3 bg-muted/50 border-border">
                        <p className="text-xs text-muted-foreground">
                          <Users className="w-3 h-3 inline mr-1" />
                          Want to add guests and split costs? <Button variant="link" className="text-primary p-0 h-auto text-xs" onClick={() => navigate('/register')}>Sign up</Button> to unlock member perks (free, automatic membership).
                        </p>
                      </Card>
                    </div>
                  ) : null}

                  {/* Price Summary */}
                  {priceBreakdown && (
                    <Card className="p-6 bg-card border-border">
                      <h3 className="text-xl font-semibold mb-4">Price Summary</h3>
                      <div className="space-y-2">
                        <div className="pricing-row">
                          <span>Base Rate ({primarySnake?.name})</span>
                          <span>${priceBreakdown.breakdown.base_rate}/hr × {durationHours}hr</span>
                        </div>
                        {(rentalType === 'music_video' || rentalType === 'educational') && priceBreakdown.breakdown.total_snakes > 1 && (
                          <div className="pricing-row">
                            <span>Total Snakes: {priceBreakdown.breakdown.total_snakes}</span>
                            <span>
                              {priceBreakdown.breakdown.additional_rate === 0 
                                ? 'No extra charge' 
                                : `+$${priceBreakdown.breakdown.additional_rate}/hr`}
                            </span>
                          </div>
                        )}
                        {rentalType === 'other' && additionalSnakeIds.length > 0 && (
                          <div className="pricing-row">
                            <span>Additional Snakes ({additionalSnakeIds.length})</span>
                            <span>${priceBreakdown.breakdown.additional_rate}/hr × {durationHours}hr</span>
                          </div>
                        )}
                        <div className="pt-4 mt-4 border-t border-border">
                          <div className="flex justify-between items-center">
                            <span className="text-lg font-semibold">Total</span>
                            <span className="pricing-total">${priceBreakdown.total.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(3)} data-testid="rental-step4-back">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmitRental}
                    disabled={submitting || (!isAuthenticated && (!guestName || !guestEmail))}
                    className="btn-primary"
                    data-testid="rental-step4-submit"
                  >
                    {submitting ? (
                      <span className="flex items-center">
                        <div className="spinner mr-2" />
                        Creating Rental...
                      </span>
                    ) : (
                      <>
                        Continue to Payment
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 5: Payment */}
            {step === 5 && rental && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-8"
              >
                <div className="text-center">
                  <h1 className="text-4xl font-bold mb-4">Complete Payment</h1>
                  <p className="text-muted-foreground">Pay securely with card, Google Pay, or Apple Pay</p>
                </div>

                <Card className="p-6 bg-card border-border max-w-lg mx-auto" data-testid="rental-payment-card">
                  <div className="text-center mb-6">
                    <p className="text-muted-foreground">Total Amount</p>
                    <p className="text-4xl font-bold text-primary" data-testid="rental-payment-total">
                      ${rental.total_price.toFixed(2)}
                    </p>
                  </div>

                  {/* Cost Split Breakdown */}
                  {rental.guests && rental.guests.length > 0 && (
                    <div className="mb-6 p-4 bg-primary/10 rounded-lg border border-primary/30" data-testid="rental-payment-cost-split">
                      <h4 className="font-semibold mb-3 flex items-center">
                        <Users className="w-4 h-4 mr-2" />
                        Cost Split ({rental.guests.length + 1} people)
                      </h4>
                      <div className="space-y-2">
                        {calculateSplit(rental.total_price, rental.guests.length + 1).map((amount, i) => (
                          <div key={i} className="flex justify-between text-sm">
                            <span>{i === 0 ? `You (${user?.name || 'Member'})` : rental.guests[i - 1]}</span>
                            <span className="font-medium">${amount.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-3 border-t border-primary/20 pt-2">
                        Full payment is collected now. Share the split details with your guests.
                      </p>
                    </div>
                  )}

                  <div className="space-y-4">
                    <Button
                      onClick={handleStripePayment}
                      disabled={processingPayment}
                      className="w-full btn-primary h-14 text-lg"
                      data-testid="rental-stripe-pay-btn"
                    >
                      {processingPayment ? (
                        <span className="flex items-center">
                          <div className="spinner mr-2" />
                          Redirecting to Payment...
                        </span>
                      ) : (
                        <>
                          <CreditCard className="mr-2 w-5 h-5" />
                          Pay ${rental.total_price.toFixed(2)}
                        </>
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-3 text-muted-foreground text-xs pt-2">
                      <span>Card</span>
                      <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                      <span>Google Pay</span>
                      <span className="w-1 h-1 rounded-full bg-muted-foreground" />
                      <span>Apple Pay</span>
                    </div>

                    <p className="text-xs text-center text-muted-foreground">
                      Secure payment powered by Stripe
                    </p>
                  </div>
                </Card>

                <div className="text-center">
                  <Button variant="ghost" onClick={() => setStep(4)} data-testid="rental-step5-back">
                    <ArrowLeft className="mr-2 w-4 h-4" />
                    Back to Details
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default RentalsPage;
